import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { BadgeShelf } from "@/components/BadgeShelf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, TrendingUp, Stethoscope, FileText, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { PointsLedger } from "@/components/PointsLedger";

export default function ReportCard() {
  const { studentId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);

  useEffect(() => {
    http.get(`/report/${studentId}`)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load report"));
  }, [studentId]);

  const grouped = useMemo(() => {
    if (!report) return [];
    const map = {};
    report.metrics.forEach((m) => {
      if (!map[m.category]) map[m.category] = [];
      map[m.category].push(m);
    });
    return Object.entries(map);
  }, [report]);

  const bmiTrend = useMemo(() => {
    if (!report) return [];
    const bmi = report.metrics.find((m) => m.name === "BMI");
    if (!bmi) return [];
    return report.terms.map((t) => ({
      term: `T${t.term}`,
      student: t.values?.[bmi.id]?.student ?? null,
      class_avg: t.values?.[bmi.id]?.class_avg ?? null,
    }));
  }, [report]);

  if (!report) return <AppShell title="Loading..." />;

  const s = report.student;
  const g = s.gamification;

  return (
    <AppShell
      subtitle={`Class ${s.grade}${s.section} · ${s.kid_id}`}
      title={`${s.name}'s Report Card`}
      right={
        <div className="flex items-center gap-3 no-print">
          {s.gender && s.gender !== "any" && (
            <Badge variant="outline" className="capitalize">{s.gender}</Badge>
          )}
          <Button
            data-testid="report-pdf-btn"
            variant="outline"
            onClick={() => window.print()}
            title="Download or print this Report Card"
          >
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          <Button data-testid="report-back-btn" variant="outline" onClick={() => nav(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      }
    >
      {/* Print-only header — KOI branded, replaces the app-shell chrome on paper. */}
      <div className="print-only mb-6 pb-4 border-b-2 border-sky-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/koi-logo.png" alt="KOI" style={{ height: 44 }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-sky-700 font-bold">The School Doctor</div>
              <div className="font-heading font-bold text-slate-900 text-lg leading-tight">Health Report Card</div>
            </div>
          </div>
          <div className="text-right text-[11px] text-slate-500">
            <div className="font-mono">{new Date().toISOString().slice(0, 10)}</div>
            <div>Preventive Healthcare through School Screening</div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-700">
          <div>
            <span className="uppercase tracking-widest text-slate-400 font-bold mr-2">Student</span>
            <span className="font-semibold">{s.name}</span>
          </div>
          <div>
            <span className="uppercase tracking-widest text-slate-400 font-bold mr-2">KID ID</span>
            <span className="font-mono">{s.kid_id}</span>
          </div>
          <div>
            <span className="uppercase tracking-widest text-slate-400 font-bold mr-2">Class</span>
            <span className="font-semibold">{s.grade}{s.section}</span>
          </div>
        </div>
      </div>

      <div className="report-print-root">
      {/* Hero */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <Card className="lg:col-span-2 p-6 relative overflow-hidden">
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 rounded-full overflow-hidden" style={{ boxShadow: `0 0 0 4px ${g.current.color}` }}>
              {s.avatar_url ? <img src={s.avatar_url} alt={s.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-sky-100 grid place-items-center text-3xl font-bold text-sky-700">{s.name.charAt(0)}</div>}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.22em] font-bold" style={{ color: g.current.color }}>Level {g.current.level}</div>
              <div className="font-heading font-bold text-2xl text-slate-900 mt-1">{g.current.name}</div>
              <div className="text-sm text-slate-500 mt-1">{g.points.toLocaleString()} points earned{g.next ? ` · ${g.points_to_next.toLocaleString()} to ${g.next.name}` : " · MAX level"}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> BMI Trend</div>
          <div className="h-24 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={bmiTrend}>
                <XAxis dataKey="term" stroke="#94A3B8" fontSize={11} />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Line type="monotone" dataKey="student" stroke="#0EA5E9" strokeWidth={2.5} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="class_avg" stroke="#94A3B8" strokeDasharray="4 4" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Report Card table */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading font-semibold text-xl text-slate-900 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-sky-600" /> 22-Parameter Screening</h2>
        <div className="text-xs uppercase tracking-widest text-slate-400 hidden md:block">WHO · Class Avg · Student · across Term 1-2</div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="report-table" className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-3 py-3 font-semibold uppercase text-[10px] tracking-widest">#</th>
                <th className="text-left px-3 py-3 font-semibold uppercase text-[10px] tracking-widest">Category</th>
                <th className="text-left px-3 py-3 font-semibold uppercase text-[10px] tracking-widest">Metric</th>
                <th className="text-left px-3 py-3 font-semibold uppercase text-[10px] tracking-widest">WHO Target</th>
                {report.terms.map((t) => (
                  <th key={t.term} colSpan={2} className="text-center px-3 py-3 font-semibold uppercase text-[10px] tracking-widest border-l border-slate-200">Term {t.term}</th>
                ))}
              </tr>
              <tr className="text-[10px] uppercase tracking-widest">
                <th></th><th></th><th></th><th></th>
                {report.terms.map((t) => (
                  <Fragment key={`hdrsub-${t.term}`}>
                    <th className="px-2 py-2 text-slate-400 font-medium border-l border-slate-200 text-center">Class</th>
                    <th className="px-2 py-2 text-slate-500 font-semibold text-center">Student</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.map(([category, metrics], gi) => (
                <Fragment key={category}>
                  <tr className="bg-sky-50/60">
                    <td colSpan={4 + report.terms.length * 2} className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-sky-700">{category}</td>
                  </tr>
                  {metrics.map((m, idx) => (
                    <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-3 py-2.5 text-slate-400 tabular-nums">{gi * 3 + idx + 1}</td>
                      <td className="px-3 py-2.5 text-slate-500">{m.category}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{m.name}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline" className="text-[10px] font-mono">{m.resolved_target || m.who_target || "—"}</Badge>
                      </td>
                      {report.terms.map((t) => {
                        const v = t.values?.[m.id] || {};
                        return (
                          <Fragment key={`vals-${t.term}-${m.id}`}>
                            <td className="px-2 py-2.5 text-center text-slate-400 border-l border-slate-100 tabular-nums">{v.class_avg ?? "—"}</td>
                            <td className="px-2 py-2.5 text-center font-semibold text-sky-700 tabular-nums">{v.student ?? "—"}</td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-slate-50/50 border-t border-slate-200 text-xs">
          {report.terms.map((t) => (
            <div key={t.term} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="uppercase tracking-widest text-[10px] font-bold text-slate-400">Term {t.term}</div>
              <div className="mt-2 space-y-1">
                <div><span className="text-slate-400">Date:</span> <span className="text-slate-700 font-medium">{t.date || "—"}</span></div>
                <div><span className="text-slate-400">Teacher:</span> <span className="text-slate-700 font-medium">{t.teacher_sign || "—"}</span></div>
                <div><span className="text-slate-400">Doctor:</span> <span className="text-slate-700 font-medium">{t.doctor_sign || "—"}</span></div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Prescription leaflets — one per term, doctor's remarks */}
      {report.terms.some((t) => t.remarks) && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="prescription-leaflets">
          {report.terms.filter((t) => t.remarks).map((t) => (
            <div
              key={t.term}
              data-testid={`prescription-term-${t.term}`}
              className="relative bg-amber-50 border border-amber-200 rounded-xl overflow-hidden shadow-sm"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, transparent, transparent 30px, rgba(214, 158, 46, 0.08) 30px, rgba(214, 158, 46, 0.08) 31px)",
              }}
            >
              {/* Rx header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-amber-200/60">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-white border border-amber-300 flex items-center justify-center shadow-inner">
                    <Stethoscope className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-amber-900 text-sm leading-tight">Doctor&apos;s Note</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700/70 font-semibold">Term {t.term} Prescription · Rx</div>
                  </div>
                </div>
                <div className="text-right text-[11px] text-amber-800/80">
                  <div className="font-mono">{t.date || "—"}</div>
                  <div className="text-[10px]">Signed by {t.doctor_sign || "Dr. KOI"}</div>
                </div>
              </div>
              {/* Rx body */}
              <div className="px-5 py-4">
                <div className="text-[10px] uppercase tracking-widest text-amber-700/70 font-bold mb-2 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Remarks &amp; Recommendations
                </div>
                <div className="font-serif text-slate-800 text-[15px] leading-relaxed whitespace-pre-wrap">
                  {t.remarks}
                </div>
              </div>
              {/* Perforated tear-line footer */}
              <div className="border-t border-dashed border-amber-300/70 px-5 py-2 bg-amber-100/40 flex items-center justify-between text-[10px] uppercase tracking-widest text-amber-800/70 font-semibold">
                <span>koi · The School Doctor</span>
                <span>Non-clinical · For guidance only</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Previous school records (from approved port requests) */}
      {report.historic && report.historic.length > 0 && (
        <div className="mt-10" data-testid="historic-records">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-semibold text-xl text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" /> Previous School Records
              </h2>
              <div className="text-xs text-slate-500 mt-0.5">Ported over from your child&apos;s earlier school health card.</div>
            </div>
            <Badge variant="outline" className="uppercase tracking-widest text-[10px] bg-amber-50 text-amber-700 border-amber-200">
              {report.historic.length} archived term{report.historic.length > 1 ? "s" : ""}
            </Badge>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {report.historic.map((h) => {
              const filledCount = Object.values(h.values || {}).filter((v) => v !== null && v !== "" && v !== undefined).length;
              return (
                <div
                  key={h.id}
                  data-testid={`historic-card-${h.id}`}
                  className="rounded-xl border border-slate-300 bg-white shadow-sm overflow-hidden"
                >
                  {/* Header — pale blue prescription style */}
                  <div className="px-5 py-3 bg-slate-100/60 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <div className="font-heading font-bold text-slate-900 text-sm leading-tight">{h.previous_school || "Previous school"}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mt-0.5">
                        Session {h.session} · Term {h.term}
                      </div>
                    </div>
                    <div className="text-right text-[11px] text-slate-500">
                      <div className="font-mono">{h.date || "—"}</div>
                      {h.doctor_sign && <div className="text-[10px]">Signed by {h.doctor_sign}</div>}
                    </div>
                  </div>
                  {/* Values grid */}
                  {filledCount > 0 && (
                    <div className="px-5 py-3">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Measurements ({filledCount})</div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {report.metrics.map((m) => {
                          const v = h.values?.[m.id];
                          if (v === null || v === undefined || v === "") return null;
                          return (
                            <div key={m.id} className="flex justify-between border-b border-dashed border-slate-200 py-1">
                              <span className="text-slate-600">{m.name}</span>
                              <span className="font-mono font-semibold text-slate-800">{v}{m.unit ? ` ${m.unit}` : ""}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Remarks */}
                  {h.remarks && (
                    <div className="px-5 py-3 bg-amber-50/60 border-t border-slate-200">
                      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">Doctor&apos;s remarks</div>
                      <div className="font-serif text-slate-800 text-sm leading-relaxed">{h.remarks}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Badges */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-xl text-slate-900">Journey to KOI Legend</h2>
          <div className="text-xs uppercase tracking-widest text-slate-400">{g.current.name}</div>
        </div>
        <BadgeShelf points={s.points || 0} testId="report-badge-shelf" />
      </div>

      <div className="mt-8 no-print">
        <PointsLedger entityType="student" entityId={s.id} title="How these points were earned" />
      </div>

      {/* Print-only footer with KOI branding + disclaimer. */}
      <div className="print-only mt-8 pt-3 border-t border-slate-300 text-[10px] text-slate-500 flex items-center justify-between">
        <span className="uppercase tracking-[0.22em] font-bold text-sky-700">koi · The School Doctor</span>
        <span>Non-clinical · For preventive-care guidance only</span>
      </div>

      </div>{/* /report-print-root */}
    </AppShell>
  );
}
